export interface DotGridOptions {
  /**
   * Element to render into. If omitted, the canvas is mounted on `document.body`
   * as a fixed full-viewport background that does not affect layout or pointer
   * events anywhere on the page.
   */
  container?: HTMLElement;

  /** Distance between dots in CSS pixels. */
  spacing?: number;

  /** Idle dot radius in CSS pixels. */
  dotMin?: number;

  /** Maximum dot radius when the cursor is directly over a dot. */
  dotMax?: number;

  /** Pixel radius of the cursor's influence area. */
  radiusEffect?: number;

  /** Idle alpha (0-1). Set to 0 to fully hide dots until hovered. */
  baseAlpha?: number;

  /** Peak alpha when the cursor is directly over a dot (0-1). */
  maxAlpha?: number;

  /** Dot color as an `r,g,b` string (e.g. `"180,178,169"`). */
  color?: string;

  /** Per-frame interpolation factor (0-1). Higher = snappier. */
  smoothing?: number;

  /** CSS `z-index` applied to the canvas. */
  zIndex?: number;

  /** Start the animation immediately. Defaults to `true`. */
  autoStart?: boolean;
}

interface Dot {
  x: number;
  y: number;
  size: number;
  alpha: number;
}

type ResolvedOptions = Required<Omit<DotGridOptions, 'container'>>;

const DEFAULTS: ResolvedOptions = {
  spacing: 22,
  dotMin: 3,
  dotMax: 12,
  radiusEffect: 220,
  baseAlpha: 0,
  maxAlpha: 0.85,
  color: '180,178,169',
  smoothing: 0.12,
  zIndex: 0,
  autoStart: true,
};

export class DotGrid {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly container: HTMLElement;
  private readonly isFullscreen: boolean;
  private readonly dpr: number;
  private opts: ResolvedOptions;
  private dots: Dot[] = [];
  private mouseX = -9999;
  private mouseY = -9999;
  private rafId: number | null = null;
  private resizeObserver: ResizeObserver | null = null;

  constructor(options: DotGridOptions = {}) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new Error('DotGrid requires a browser environment.');
    }

    this.opts = { ...DEFAULTS, ...options };
    this.container = options.container ?? document.body;
    this.isFullscreen = !options.container;
    this.dpr = window.devicePixelRatio || 1;

    this.canvas = document.createElement('canvas');
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('DotGrid: 2D canvas context unavailable.');
    this.ctx = ctx;

    this.applyCanvasStyles();
    this.container.appendChild(this.canvas);

    window.addEventListener('mousemove', this.onMouseMove, { passive: true });
    window.addEventListener('mouseout', this.onMouseOut, { passive: true });
    window.addEventListener('blur', this.onMouseOut);

    if (this.isFullscreen) {
      window.addEventListener('resize', this.resize);
    } else if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.resize);
      this.resizeObserver.observe(this.container);
    }

    this.resize();

    if (this.opts.autoStart) this.start();
  }

  /** Start the animation loop. No-op if already running. */
  start(): void {
    if (this.rafId !== null) return;
    this.rafId = requestAnimationFrame(this.tick);
  }

  /** Pause the animation loop. */
  stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /**
   * Update one or more options at runtime. Triggers a regeneration of the dot
   * grid if layout-affecting options (`spacing`) change.
   */
  setOptions(options: Partial<DotGridOptions>): void {
    const prevSpacing = this.opts.spacing;
    this.opts = { ...this.opts, ...options };
    if (options.zIndex !== undefined) {
      this.canvas.style.zIndex = String(options.zIndex);
    }
    if (options.spacing !== undefined && options.spacing !== prevSpacing) {
      this.resize();
    }
  }

  /** Remove the canvas, listeners, and stop the loop. */
  destroy(): void {
    this.stop();
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseout', this.onMouseOut);
    window.removeEventListener('blur', this.onMouseOut);
    window.removeEventListener('resize', this.resize);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.canvas.remove();
    this.dots = [];
  }

  private applyCanvasStyles(): void {
    const s = this.canvas.style;
    if (this.isFullscreen) {
      s.position = 'fixed';
      s.top = '0';
      s.left = '0';
      s.width = '100vw';
      s.height = '100vh';
    } else {
      s.position = 'absolute';
      s.inset = '0';
      s.width = '100%';
      s.height = '100%';
      const cs = getComputedStyle(this.container);
      if (cs.position === 'static') {
        this.container.style.position = 'relative';
      }
    }
    s.pointerEvents = 'none';
    s.zIndex = String(this.opts.zIndex);
    s.display = 'block';
  }

  private getViewport(): { w: number; h: number } {
    if (this.isFullscreen) {
      return { w: window.innerWidth, h: window.innerHeight };
    }
    const rect = this.container.getBoundingClientRect();
    return { w: rect.width, h: rect.height };
  }

  private resize = (): void => {
    const { w, h } = this.getViewport();
    if (w <= 0 || h <= 0) return;

    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    const { spacing, dotMin, baseAlpha } = this.opts;
    const cols = Math.max(1, Math.floor(w / spacing));
    const rows = Math.max(1, Math.floor(h / spacing));
    const offX = (w - (cols - 1) * spacing) / 2;
    const offY = (h - (rows - 1) * spacing) / 2;

    this.dots = new Array(cols * rows);
    let i = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        this.dots[i++] = {
          x: offX + c * spacing,
          y: offY + r * spacing,
          size: dotMin,
          alpha: baseAlpha,
        };
      }
    }
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (this.isFullscreen) {
      this.mouseX = e.clientX;
      this.mouseY = e.clientY;
    } else {
      const rect = this.container.getBoundingClientRect();
      this.mouseX = e.clientX - rect.left;
      this.mouseY = e.clientY - rect.top;
    }
  };

  private onMouseOut = (): void => {
    this.mouseX = -9999;
    this.mouseY = -9999;
  };

  private tick = (): void => {
    const { w, h } = this.getViewport();
    this.ctx.clearRect(0, 0, w, h);

    const {
      dotMin,
      dotMax,
      radiusEffect,
      baseAlpha,
      maxAlpha,
      color,
      smoothing,
    } = this.opts;
    const sizeRange = dotMax - dotMin;
    const alphaRange = maxAlpha - baseAlpha;
    const mx = this.mouseX;
    const my = this.mouseY;

    for (const d of this.dots) {
      const dx = d.x - mx;
      const dy = d.y - my;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const t = Math.max(0, 1 - dist / radiusEffect);
      const influence = t * t;

      d.size += (dotMin + sizeRange * influence - d.size) * smoothing;
      d.alpha += (baseAlpha + alphaRange * influence - d.alpha) * smoothing;

      if (d.alpha < 0.001) continue;

      this.ctx.beginPath();
      this.ctx.arc(d.x, d.y, d.size, 0, Math.PI * 2);
      this.ctx.fillStyle = `rgba(${color},${d.alpha})`;
      this.ctx.fill();
    }

    this.rafId = requestAnimationFrame(this.tick);
  };
}

/** Convenience factory matching the class constructor. */
export function createDotGrid(options?: DotGridOptions): DotGrid {
  return new DotGrid(options);
}

export default DotGrid;
